// ** React Imports
import { useCallback, useMemo } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Custom Components
import FoundResultsCountText from "../FoundResultsCountText";
import SelectedElement from "components/other/SelectedElement";
import Block from "components/layout/Block";
import Text from "components/typography/Text";

// ** Users Components
import ChangeFiltersButton from "../ChangeFiltersButton";

// ** Custom Filters Data
import useSexFilterData from "./components/useSexFilterData";
import useSexCoupleFilterData from "./components/useSexCoupleFilterData";
import useAgeFilterData from "./components/useAgeFilterData";
import useSearchSexFilterData from "./components/useSearchSexFilterData";
import useSearchPurposeFilterData from "./components/useSearchPurposeFilterData";
import useLookingAgeFilterData from "./components/useLookingAgeFilterData";
import useContentFilterData from "./components/useContentFilterData";
import useLanguageFilterData from "./components/useLanguageFilterData";
import useHeightFilterData from "./components/useHeightFilterData";
import useFigureFilterData from "./components/useFigureFilterData";
import useZodiacFilterData from "./components/useZodiacFilterData";
import useHairFilterData from "./components/useHairFilterData";
import useRelationshipFilterData from "./components/useRelationshipFilterData";
import useKidsFilterData from "./components/useKidsFilterData";
import useDrinkingFilterData from "./components/useDrinkingFilterData";
import useSmokingFilterData from "./components/useSmokingFilterData";
import useEducationFilterData from "./components/useEducationFilterData";
import useLocationFilterData from "./components/useLocationFilterData";
import useProfileNameFilterData from "./components/useProfileNameFilterData";
import usePreferencesFilterData from "./components/usePreferencesFilterData";
import useInterestsFilterData from "./components/useInterestsFilterData";

// #####################################################

const ResultsDescription = ({
	filtersOpen,
	setFiltersOpen,
	hideChangeFiltersButton,
	locationPathname,
	locationSearch,
}) => {
	const { t } = useTranslation(["common", "filtersAndSelectors"]);

	const language = useSelector((state) => state.globalCommon.language);

	const sexFilterData = useSexFilterData();
	const sexCoupleFilterData = useSexCoupleFilterData();
	const ageFilterData = useAgeFilterData();
	const locationFilterData = useLocationFilterData();
	const searchSexFilterData = useSearchSexFilterData();
	const searchPurposeFilterData = useSearchPurposeFilterData();
	const lookingAgeFilterData = useLookingAgeFilterData();
	const contentFilterData = useContentFilterData();
	const preferencesFilterData = usePreferencesFilterData();
	const interestFilterData = useInterestsFilterData();
	const languageFilterData = useLanguageFilterData();
	const heightFilterData = useHeightFilterData();
	const figureFilterData = useFigureFilterData();
	const zodiacFilterData = useZodiacFilterData();
	const hairFilterData = useHairFilterData();
	const relationshipFilterData = useRelationshipFilterData();
	const kidsFilterData = useKidsFilterData();
	const drinkingFilterData = useDrinkingFilterData();
	const smokingFilterData = useSmokingFilterData();
	const educationFilterData = useEducationFilterData();
	const profileNameFilterData = useProfileNameFilterData();

	// ** Filtry - Lista
	const filters = useMemo(() => {
		const filtersArray = [
			sexFilterData,
			sexCoupleFilterData,
			ageFilterData,
			locationFilterData,
			searchSexFilterData,
			searchPurposeFilterData,
			lookingAgeFilterData,
			contentFilterData,
			preferencesFilterData,
			interestFilterData,
			languageFilterData,
			heightFilterData,
			figureFilterData,
			zodiacFilterData,
			hairFilterData,
			relationshipFilterData,
			kidsFilterData,
			drinkingFilterData,
			smokingFilterData,
			educationFilterData,
			profileNameFilterData,
		];
		return [...filtersArray.filter((x) => Boolean(x))];
	}, [
		locationPathname,
		locationSearch,
		locationFilterData?.selected,
		language,
	]);

	const renderSelectedFilters = useCallback(() => {
		return filters.map((filter, index) => (
			<SelectedElement
				key={filter?.label || index}
				data={filter}
				isLastFilter={filters.length === index + 1}
			/>
		));
	}, [filters]);

	const foundUsersCount = useSelector(
		(state) => state.mainPageUsers.findnumber
	);

	// #####################################################

	return (
		<Block flex alignCenter>
			<Block>
				<FoundResultsCountText
					resultsType="users"
					resultsCount={foundUsersCount}
				/>
				{filters?.length ? (
					<Text block mr={1}>
						{t(`filtersAndSelectors:inCategory`)}
					</Text>
				) : null}
				{renderSelectedFilters()}
				{hideChangeFiltersButton ? null : (
					<ChangeFiltersButton
						filtersOpen={filtersOpen}
						setFiltersOpen={setFiltersOpen}
					/>
				)}
			</Block>
		</Block>
	);
};

// #####################################################

export default ResultsDescription;

// #####################################################
